/**
 * Email-related utility functions for backend API integration
 * 
 * These utilities handle URL encoding and validation for email addresses
 * used as user identifiers in backend API calls.
 */

/**
 * Encode email address for use in URL paths
 * 
 * @param email - Email address to encode
 * @returns URL-encoded email address safe for use in API paths
 * 
 * @example
 * ```
 * encodeEmailForUrl('AbdulKhan@rich.com') // Returns 'AbdulKhan%40rich.com'
 * ```
 */
export function encodeEmailForUrl(email: string): string {
    if (!email) {
        throw new Error('Email address is required');
    }

    // Trim whitespace and convert to lowercase for consistency
    const normalizedEmail = email.trim().toLowerCase();

    // Validate basic email format
    if (!isValidEmailFormat(normalizedEmail)) {
        throw new Error(`Invalid email format: ${email}`);
    }

    // URL encode the email address
    return encodeURIComponent(normalizedEmail);
}

/**
 * Decode URL-encoded email address
 * 
 * @param encodedEmail - URL-encoded email address
 * @returns Decoded email address
 * 
 * @example
 * ```
 * decodeEmailFromUrl('AbdulKhan%40rich.com') // Returns 'abdulkhan@rich.com'
 * ```
 */
export function decodeEmailFromUrl(encodedEmail: string): string {
    if (!encodedEmail) {
        throw new Error('Encoded email address is required');
    }

    try {
        return decodeURIComponent(encodedEmail).toLowerCase();
    } catch (error) {
        throw new Error(`Failed to decode email address: ${encodedEmail}`);
    }
}

/**
 * Validate basic email format
 * 
 * @param email - Email address to validate
 * @returns True if email format is valid
 */
export function isValidEmailFormat(email: string): boolean {
    if (!email || typeof email !== 'string') {
        return false;
    }

    // Basic email regex - matches most common email formats
    // This is not RFC-compliant but sufficient for Azure AD emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Extract email from Azure AD account object
 * 
 * @param account - Azure AD account object
 * @returns Email address or null if not found
 */
export function extractEmailFromAccount(account: any): string | null {
    if (!account) {
        return null;
    }

    // Azure AD provides email in multiple possible fields
    const email = account.username ||
        account.email ||
        account.idTokenClaims?.email ||
        account.idTokenClaims?.preferred_username ||
        account.idTokenClaims?.upn;

    return email ? email.toLowerCase().trim() : null;
}

/**
 * Normalize email address for consistent usage
 * 
 * @param email - Email address to normalize
 * @returns Normalized email address (lowercase, trimmed)
 */
export function normalizeEmail(email: string): string {
    if (!email) {
        throw new Error('Email address is required');
    }

    return email.trim().toLowerCase();
}